import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../../models/NameUtils';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../UserCombobox';
import { styleSheet } from './styles';

interface IProps {
	/** The logged-in (or impersonated) user — the implicit "me" default. */
	currentUser: Api.IUser | null;
	selectedHost: Api.IUser | null;
	setSelectedHost: React.Dispatch<React.SetStateAction<Api.IUser | null>>;
	anchorClassName?: string;
	/** Admins with scheduler reporting may search/pick any host; others are limited to delegators. */
	canBrowseAll: boolean;
	/** Users who delegated meeting admin to the current user — pickable without `canBrowseAll`. */
	delegatedUsers: Api.IUser[];
	/** True as soon as the delegation grant is known, before `delegatedUsers` resolves to names. */
	hasDelegatedAccess: boolean;
}

export const MeetingHostFilterDropdown = ({
	currentUser,
	selectedHost,
	setSelectedHost,
	anchorClassName,
	canBrowseAll,
	delegatedUsers,
	hasDelegatedAccess,
}: IProps) => {
	const id = React.useId();

	// Delegation is a fixed roster rather than an account-wide search, so "me" has to be an
	// explicit option for switching back.
	const delegatedOptions = [
		...(currentUser ? [currentUser] : []),
		...delegatedUsers.filter(user => user.id !== currentUser?.id),
	];
	const isReadOnly = !canBrowseAll && !hasDelegatedAccess;

	return (
		<div className={`${anchorClassName ?? ''}${isReadOnly ? ` ${css(styleSheet.disabledWrapper)}` : ''}`}>
			{canBrowseAll ? (
				<UserCombobox id={selectedHost?.id} key={selectedHost?.id} onValueChange={setSelectedHost}>
					<ComboboxInput
						id={`${id}-host-combobox`}
						placeholder={selectedHost ? getDisplayName(selectedHost) : 'Host'}
						showClear={!!selectedHost}
						styles={[styleSheet.comboboxInput]}
					/>
					<ComboboxContent styles={[styleSheet.comboboxContent]}>
						<UserComboboxStatus />
						<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
			) : (
				<Combobox
					key={selectedHost?.id ?? 'none'}
					items={delegatedOptions}
					itemToStringLabel={getDisplayName}
					isItemEqualToValue={(item: Api.IUser, value: Api.IUser) => item.id === value.id}
					onValueChange={setSelectedHost}
				>
					<ComboboxInput
						id={`${id}-host-combobox`}
						placeholder={selectedHost ? getDisplayName(selectedHost) : 'Host'}
						styles={[styleSheet.comboboxInput]}
						disabled={isReadOnly}
					/>
					<ComboboxContent styles={[styleSheet.comboboxContent]}>
						<ComboboxEmpty>No users found</ComboboxEmpty>
						<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</Combobox>
			)}
		</div>
	);
};
